"use client";

import { useEffect } from "react";
import { body, display } from "./fonts";
// global-error REPLACES the root layout (src/app/layout.tsx) when it fires, so
// none of that layout's assets — including this stylesheet — ship with it.
// Import it directly or the page renders completely unstyled: no cream ground,
// no radial washes, no tokens.
import "./globals.css";

// layout.tsx puts these two `.variable` classNames on <html> so globals.css's
// `--font-display` / `--font-body` resolve to Fraunces and Mulish. This file
// renders its own <html>, so it imports both faces from `./fonts` and hangs
// them there too, or the heading silently falls back to Georgia and the copy to
// system-ui while the rest of the app looks right.

// Real gold focus ring rather than the default browser outline, matching the
// one primary action on /signin. Scoped to this page's button.
const focusStyles = `
.error-cta:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--color-cream), 0 0 0 6px var(--color-gold);
}
`;

/**
 * Root-layout error boundary: fires when layout.tsx itself throws, or when a
 * throw elsewhere has no error.tsx above it to land in. Savor is installed to
 * home screens, so this is the difference between the app showing its own calm
 * page and WebKit showing "This page couldn't load".
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    void (async () => {
      try {
        const { reportError } = await import("@/lib/flare");
        await reportError(error, { kind: "client", digest: error.digest });
      } catch {
        // The page is already broken; a failed report must not make it worse.
      }
    })();
  }, [error]);

  return (
    <html lang="en" className={`${display.variable} ${body.variable}`}>
      <head>
        {/* Next's global-error path bypasses metadata/viewport resolution
            entirely (its own DefaultGlobalError has the same gap). Without this
            the installed PWA renders this page at desktop width — exactly the
            moment the file exists for. */}
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
      </head>
      <body>
        <style dangerouslySetInnerHTML={{ __html: focusStyles }} />

        <main
          style={{
            minHeight: "100dvh", display: "flex", alignItems: "center", justifyContent: "center",
            padding: "40px 22px calc(40px + env(safe-area-inset-bottom))",
          }}
        >
          <div className="width-reading" style={{ width: "100%", textAlign: "center" }}>
            {/* The app's own sun mark, drawn rather than an emoji. */}
            <svg
              viewBox="0 0 512 512" width="64" height="64" aria-hidden="true"
              style={{ display: "block", margin: "0 auto 22px" }}
            >
              <circle cx="256" cy="256" r="156" fill="var(--color-surface)" />
              <g fill="var(--color-gold)">
                {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((deg) => (
                  <path key={deg} d="M256 30 L239 98 L273 98 Z" transform={`rotate(${deg} 256 256)`} />
                ))}
              </g>
              <circle
                cx="256" cy="256" r="146" fill="none"
                stroke="var(--color-gold)" strokeWidth="7" opacity="0.55"
              />
            </svg>

            <h1 style={{ fontSize: "clamp(28px, 7vw, 38px)", lineHeight: 1.15, margin: 0,
              color: "var(--color-ink)" }}>
              Savor hit a snag
            </h1>
            <p style={{ color: "var(--color-ink-soft)", fontSize: "var(--text-body)",
              lineHeight: 1.6, margin: "12px 0 0" }}>
              Something went wrong on our side, and nothing you did caused it.
              Trying again usually sorts it out.
            </p>

            <button
              type="button"
              onClick={() => reset()}
              className="hover-deepen error-cta"
              style={{ marginTop: 30,
                background: "linear-gradient(150deg,var(--color-gold-sparkle),var(--color-gold))",
                color: "var(--color-on-gold)", fontWeight: 800, border: "none", borderRadius: "var(--radius-tile)",
                padding: "15px 28px", minHeight: 48, fontSize: 16, cursor: "pointer",
                boxShadow: "var(--shadow-sm)" }}
            >
              Try again
            </button>
          </div>
        </main>
      </body>
    </html>
  );
}
